/**
 * React component that renders the mini Airbnb card
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'reactstrap'
import MiniCard from '../MiniCard'
import { Title, Body, BtnLink } from '../MiniCardUIComponents'
import AirbnbCoverImage from 'client/app/js/components/AirbnbCoverImage'
import DotDivider from 'client/shared/js/components/DotDivider'
import { parsePrice } from 'client/shared/js/utils/airbnb'
import styles from './MiniAirbnbCard.module.scss'

const MiniAirbnbCard = ({ data, onClick }) => {
  const image = useMemo(() => (<AirbnbCoverImage data={data} />), [data])

  const header = useMemo(() => (
    <div className={styles['header']}>
      <Button
        className={cx(styles['airbnb-btn'], 'round')}
        color='airbnb'>
        <FontAwesomeIcon className={cx('font-inherit')} icon={['fab', 'airbnb']} />
      </Button>
    </div>
  ), [])

  const body = useMemo(() => {
    const price = parsePrice(data.price)

    return (
      <div className='d-flex flex-column'>
        <div className={cx(styles['line-divider'])} />
        <div className='d-flex align-items-center'>
          <div className='font-small'>
            <span className='font-weight-semibold'>{price}</span> / night
          </div>
          <DotDivider />
          <div className='d-flex align-items-center'>
            <FontAwesomeIcon className='font-inherit color-airbnb' icon={['fas', 'star']} />
            <span className={cx('font-weight-semibold', {
              'dark-gray': !data.ratings
            })}>
              {data.ratings || '-'}
            </span>
          </div>
        </div>
      </div>
    )
  }, [data.price, data.ratings])

  const footer = useMemo(() => {
    return (
      <BtnLink title='See place details'>
        See details
      </BtnLink>
    )
  }, [])

  return (
    <MiniCard
      className={cx(styles['mini-place-card'])}
      image={image}
      header={header}
      footer={footer}
      onClick={onClick}>
      <Title>{data.title}</Title>
      <Body>{body}</Body>
    </MiniCard>
  )
}

MiniAirbnbCard.propTypes = {
  data: PropTypes.object.isRequired,
  onClick: PropTypes.func
}

MiniAirbnbCard.defaultProps = {
  onClick: () => false
}

export default MiniAirbnbCard
